import { FC, useEffect, useMemo, useRef, useState } from "react";

import { useLocation } from "react-router-dom";
import { Table } from "antd";

import { useScreen } from "common/shared-hooks";
import { useFullInvestments } from "common/shared-hooks/useFullInvestments";
import { useAltQuotesSelector } from "domains/altQuotes/store/altQuotes.selectors";
import { assetTypes } from "domains/liquidity/components/SelectAssetsTabs/SelectOtherAsset/constants";
import {
  altQuoteDesktopFundColumns,
  desktopFundColumns,
  mobileFundColumns,
} from "domains/liquidity/components/FundAssetsTable/constants";
import { TAssetType } from "domains/liquidity/shared/types";
import { currencyFormatter } from "domains/liquidity/shared/utils/inputValueFormatter";
import { setSelectedAssetsData } from "domains/liquidity/store/liquidity.reducer";
import { useLiquiditySelector } from "domains/liquidity/store/liquidity.selectors";
import { useAppDispatch } from "store/store.hooks";

type TValidAssetsState = {
  validSelectedAssets: string[];
  validSelectedAssetsData: TAssetType[];
};

const SelectAssetsForLiquidityRequest: FC<any> = ({
  _selectedAssets,
  setSelectedAssets,
  investments,
  fromProposal,
}) => {
    const dispatch = useAppDispatch();
    const { mobile } = useScreen();
    const { isAltQuoteTab } = useAltQuotesSelector();
    const { selectedAssetsForLiquidity } = useLiquiditySelector();
    const {
        getAssetsWithFundManager,
        isInvestmentInfoCompleted,
        navigateToEditAsset,
        setInvestmentInfoCompleted,
    } = useFullInvestments();
    const { search, pathname, state: locationState } = useLocation();
    
    const isLiquidityRequest = pathname.includes('new-liquidity-request');
    
    const queryParams = new URLSearchParams(search);
    const confirmAssets = queryParams.get('confirmAssets') === "true"

    const desktopColumns = isAltQuoteTab ? altQuoteDesktopFundColumns : desktopFundColumns;
    const mobileColumns = isAltQuoteTab
      ? mobileFundColumns.filter((column) => column.key !== "action")
      : mobileFundColumns;
    const columns = mobile ? mobileColumns : desktopColumns;

    const [ initialValidAssets, setInitialValidAssets ] = useState<TValidAssetsState>({
      validSelectedAssets: [],
      validSelectedAssetsData: [],
    });

    const allRowsSelectedRef = useRef(true);
    const hasInitializedRef = useRef(false);
    const userHasInteractedRef = useRef(false);
    const lastInvestmentsRef = useRef<any[]>([]);

    const assetsCompletionChanged = useMemo(() => {
        if (!lastInvestmentsRef.current.length || !investments?.length) {
            return false;
        }
        
        return investments.some((asset: any) => {
            const prevAsset = lastInvestmentsRef.current.find((prev: any) => prev.id === asset.id);
            if (!prevAsset) return false;
            
            const wasComplete = isInvestmentInfoCompleted(prevAsset);
            const isNowComplete = isInvestmentInfoCompleted(asset);
            
            return !wasComplete && isNowComplete;
        });
    }, [ investments, isInvestmentInfoCompleted ]);

    useEffect(() => {
        if (investments?.length) {
            lastInvestmentsRef.current = [ ...investments ];
        }
    }, [ investments ]);

    useEffect(() => {
        if (
          !hasInitializedRef.current &&
          Array.isArray(selectedAssetsForLiquidity) && selectedAssetsForLiquidity.length > 0 &&
          Array.isArray(investments) && investments.length > 0
        ) {
          const validData: TAssetType[] = [];
          const validAssets = selectedAssetsForLiquidity.filter((assetId: any) => {
            const investment = investments.find((inv: any) => inv.id === assetId);
            if (investment) {
              if (isLiquidityRequest) {
                validData.push({ ...investment, is_investment_info_complete: true });
                return true;
              } else if (isInvestmentInfoCompleted(investment)) {
                validData.push({ ...investment, is_investment_info_complete: true });
                return true;
              }
            }
            return false;
          });
          setInitialValidAssets({ validSelectedAssets: validAssets, validSelectedAssetsData: validData });
          dispatch(setSelectedAssetsData(validData));
          hasInitializedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [ selectedAssetsForLiquidity, investments ]);

    const { validSelectedAssets } = initialValidAssets;


    useEffect(() => {
        if (assetsCompletionChanged && investments?.length && !isLiquidityRequest) {
            const newCompleteAssets = investments.filter((asset: any) => {
                const isComplete = isInvestmentInfoCompleted(asset);
                const isAlreadySelected = validSelectedAssets.includes(asset.id);
                return isComplete && !isAlreadySelected;
            });

            if (newCompleteAssets.length > 0) {
                const newSelectedKeys = [ ...validSelectedAssets, ...newCompleteAssets.map((asset: any) => asset.id) ];
                const allSelectedInvestments = newSelectedKeys.map(
                    (key) => investments.find((investment: any) => investment.id === key),
                );

                dispatch(setSelectedAssetsData(setInvestmentInfoCompleted(allSelectedInvestments)));
                setSelectedAssets(newSelectedKeys);
                setInitialValidAssets({
                    validSelectedAssets: newSelectedKeys,
                    validSelectedAssetsData: setInvestmentInfoCompleted(allSelectedInvestments),
                });
            }
        }
    }, [
        assetsCompletionChanged,
        investments,
        validSelectedAssets,
        isLiquidityRequest,
        isInvestmentInfoCompleted,
        dispatch,
        setSelectedAssets,
        setInvestmentInfoCompleted,
    ]);

    const rowSelection: any = {
        selectedRowKeys: validSelectedAssets,
        onSelect: (record: any, selected: boolean, _selectedRows: any[]) => {
            userHasInteractedRef.current = true;
            const investment = investments.find((investment: any) => investment.id === record.key);
            if (selected && !isLiquidityRequest && !isInvestmentInfoCompleted(investment)) {
                navigateToEditAsset(investment.asset_id);
                return;
            }
            const newSelectedKeys = selected 
                ? [ ...validSelectedAssets, record.key ]
                : validSelectedAssets.filter(key => key !== record.key);
            const selectedInvestments = newSelectedKeys.map(
                (key) => investments.find((investment: any) => investment.id === key),
            );

            if (newSelectedKeys.length === 0) {
              allRowsSelectedRef.current = !allRowsSelectedRef.current;
            }

            dispatch(setSelectedAssetsData(setInvestmentInfoCompleted(selectedInvestments)));
            setSelectedAssets(newSelectedKeys);
            setInitialValidAssets({
                validSelectedAssets: newSelectedKeys,
                validSelectedAssetsData: setInvestmentInfoCompleted(selectedInvestments),
            });
        },
        onSelectAll: (selected: boolean, selectedRows: any[], _changeRows: any[]) => {
            userHasInteractedRef.current = true;
            const newSelectedValue = !allRowsSelectedRef.current;
            allRowsSelectedRef.current = newSelectedValue;
            
            let newSelectedKeys = newSelectedValue ? selectedRows.map((row: any) => row.key) : [];
            let selectedInvestments = newSelectedKeys.map(
                (key) => investments.find((investment: any) => investment.id === key),
            );
            if (!isLiquidityRequest && newSelectedValue) {
                newSelectedKeys = [];
                selectedInvestments = selectedInvestments.filter(
                    (investment: any) => {
                        const shouldBeSelected = isInvestmentInfoCompleted(investment);
                        if (shouldBeSelected) {
                            newSelectedKeys.push(investment.id); 
                        }
                        return shouldBeSelected;
                    },
                );
            }

            setSelectedAssets(newSelectedKeys);
            dispatch(setSelectedAssetsData(setInvestmentInfoCompleted(selectedInvestments)));
            setInitialValidAssets({
                validSelectedAssets: newSelectedKeys,
                validSelectedAssetsData: setInvestmentInfoCompleted(selectedInvestments),
            });
        },
    };

    const getAssetTypeName = (asset:any) => {
        if (asset.asset_type === 5) {
          const description = asset.asset_description?.length > 80 
            ? asset.asset_description.substring(0, 80) 
            : asset.asset_description;
          return description;
        }
        if (asset.asset_type === 2) {
          const description = asset.sponsor_description.length > 80 
            ? asset.sponsor_description.substring(0, 80) 
            : asset.sponsor_description;
          return description;
        }
        
        return assetTypes[ asset.asset_type ].title;
    };
    
    const getSponsorName = (asset:any) => {
        if (asset.asset_type === 2) {
          const description = asset.sponsor?.length > 80 
            ? asset.sponsor.substring(0, 80) 
            : asset.sponsor;
          return description;
        }
    };

    const fullAssets = getAssetsWithFundManager(investments);
  
    const fundDataSource = [ ...fullAssets ]?.reverse().map((asset: any) => ({
        key: asset.id,
        fund: (
            <div>
                <b>
                    <span>{asset.asset_name || asset.fund_name || getAssetTypeName(asset)}</span>
                </b>
                <br />
                <span>{asset.fund_manager || asset.sponsor || getSponsorName(asset)}</span>
                <br />
                <span className="ben-fund-assets-text">NAV: </span>
                {currencyFormatter(asset.current_net_asset_value)}
                {isAltQuoteTab && (
                    <>
                        <br />
                        <span className="ben-fund-assets-text">Unfunded Commitments: </span>
                        {currencyFormatter(asset.unfunded_commitment_currency)}
                    </>
                )}
            </div>
        ),
        fundManager: asset.fund_manager || asset.sponsor ,
        fundName: asset.asset_name || asset.fund_name,
        nav: currencyFormatter(asset.current_net_asset_value),
        unfundedCommitments: currencyFormatter(asset.unfunded_commitment_currency),
    }));

    useEffect(() => {
      // Skip auto-selection if coming from proposal - let the parent component handle asset selection
      if (fromProposal) {
        return;
      }
      
      if (!confirmAssets && !selectedAssetsForLiquidity.length && fullAssets?.length && !userHasInteractedRef.current) {
        // TODO: Add NAV filtering logic here for new AltQuotes if needed
        // For now, selecting all assets (existing behavior)
        const assetIds = fullAssets.map(({ id }: any) => id);
        setSelectedAssets(assetIds)
      }
    }, [ confirmAssets, selectedAssetsForLiquidity.length, fullAssets?.length, fromProposal ])
  
    if (!fullAssets?.length) return null;       
  
    return (
      <Table
        style={ {
          maxHeight: mobile ? "calc(100vh - 400px)" : `calc(100vh - 520px)`,
          minHeight: "170px",
          overflow: "hidden",
          overflowY: "auto",
          flex: 1,
        } }
        pagination={ false }
        columns={ columns }
        dataSource={ fundDataSource }
        rowSelection={ rowSelection }
        className="ben-fund-assets-table"
      />
    );
}

export default SelectAssetsForLiquidityRequest;
